import React, { useEffect, useCallback, useMemo } from 'react';
import { StyleSheet, RefreshControl, Pressable, View, Platform } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useCollectionStore, useAuthStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import CollectionDetailedItem from '@/components/CollectionDetailedItem';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import { useTranslation } from '@/hooks/useTranslation';
import SignInCTAPanel from '@/components/Placeholders/SignInCTAPanel';
import { buildFolderTree, flattenFolderTree, sortFolderTree } from '@/constants/utils/folder_tree';

const createStyles = (colors, isXS) => StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: colors.solid_color_primary,
  },
  content: {
    flex: 1,
    width: '100%',
    maxWidth: 800,
    paddingHorizontal: isXS ? 15 : 20,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    color: colors.text_color_secondary,
    marginBottom: 20,
  },
  retryButton: {
    padding: 10,
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 5,
  },
});

function CollectionsPage() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);
  
  // Updated store selectors
  const collectionsMap = useCollectionStore(state => state.collections);
  const collectionIds = useCollectionStore(state => state.collectionIds);
  const collectionsSort = useCollectionStore(state => state.sortOrder);
  const fetchCollections = useCollectionStore(state => state.fetchCollections);
  const isLoading = useCollectionStore(state => state.isLoading.fetch);
  const error = useCollectionStore(state => state.errors.fetch);
  const pagination = useCollectionStore(state => state.pagination);
  
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  // Get collections array from normalized state
  const collections = useMemo(() => 
    collectionIds.map(id => collectionsMap[id]),
    [collectionsMap, collectionIds]
  );

  // Skip `fetchCollections` for anonymous visitors — endpoint 401s and
  // the anon CTA panel below makes the list moot anyway.
  useEffect(() => {
    if (!hasUser) return;
    if (collectionIds.length > 0) return;
    try {
      fetchCollections();
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  }, [fetchCollections, collectionIds.length, hasUser]);

  const handleRefresh = useCallback(async () => {
    try {
      await fetchCollections(1);
    } catch (error) {
      console.error('Error refreshing collections:', error);
    }
  }, [fetchCollections]);

  const handleLoadMore = useCallback(async () => {
    if (isLoading || pagination.page >= pagination.pages) return;
    
    try {
      await fetchCollections(pagination.page + 1);
    } catch (error) {
      console.error('Error loading more collections:', error);
    }
  }, [fetchCollections, isLoading, pagination]);

  const sortedCollections = useMemo(() => {
    const compare = (a, b) => {
      if (collectionsSort === 'a-z') return (a.title || '').localeCompare(b.title || '');
      if (collectionsSort === 'z-a') return (b.title || '').localeCompare(a.title || '');
      if (collectionsSort === 'oldest') {
        return new Date(a?.updatedAt || a?.createdAt || 0).getTime() - new Date(b?.updatedAt || b?.createdAt || 0).getTime();
      }
      return new Date(b?.updatedAt || b?.createdAt || 0).getTime() - new Date(a?.updatedAt || a?.createdAt || 0).getTime();
    };
    return flattenFolderTree(sortFolderTree(buildFolderTree(collections.filter(Boolean)), compare));
  }, [collections, collectionsSort]);

  const renderItem = useCallback(({ item: collection, index, extraData }) => (
    <CollectionDetailedItem
      collection={collection}
      depth={collection.depth || 0}
      showBorder={index !== extraData.length - 1}
    />
  ), []);

  if (!hasUser) {
    return (
      <View style={styles.container}>
        <View style={styles.content}>
          <SignInCTAPanel
            icon="folder-tree"
            source="collections_index"
            title="Sign in to use Collections"
            subtitle="Collections group files and chats so Gab can pull from the same context across every conversation."
            benefits={[
              { icon: 'folder-plus', label: 'Create as many collections as you need.' },
              { icon: 'cloud-arrow-up', label: 'Drop files into a collection to share them across chats.' },
              { icon: 'message-dots', label: 'Open any collection to start a chat scoped to its files.' },
            ]}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <FlashList
          data={sortedCollections}
          renderItem={renderItem}
          estimatedItemSize={72}
          keyExtractor={item => item.id}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingTop: 20, paddingBottom: Platform.OS === 'ios' ? Math.max(insets.bottom, 20) : 20 }}
          extraData={{ length: sortedCollections.length }}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.5}
          refreshControl={
            <RefreshControl
              refreshing={isLoading && pagination.page === 1}
              onRefresh={handleRefresh}
              tintColor={colors.text_color_secondary}
              colors={[colors.text_color_secondary]}
            />
          }
          ListEmptyComponent={() => (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>
                {error ? error : isLoading ? t('common.loading') : t('pages.collections.noCollections')}
              </Text>
              {error && (
                <Pressable style={styles.retryButton} onPress={handleRefresh}>
                  <Text color='primary' weight='bold'>{t('pages.common.retry')}</Text>
                </Pressable>
              )}
            </View>
          )}
        />
      </View>
    </View>
  );
} 

function CollectionsRoute() {
  const { t } = useTranslation();
  return (
    <>
      <MetaHead title={t('pages.collections.title')} description={t('pages.collections.description')} noindex />
      <CollectionsPage />
    </>
  );
}

export default React.memo(CollectionsRoute);